import { useState } from "react";

import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaInfoCircle } from "react-icons/fa";

import { handleColumnToggle } from "@/utils/handleColumnTogle";
import {
	getIndicatorClassification,
	getIndicatorClassificationColorClassName,
	getIndicatorClassificationScores,
	IndicatorEsfEnum,
} from "@/utils/indicatorsEsf";
import { ResumeIndicatorC7ByHealthTeamData } from "@/services/esus/indicator/esf/c7/getResumeIndicatorC7ByHealthTeam";
import { Column } from "@/@types/generics/Column";
import { calculatePercentage } from "@/utils/calculatePercentage";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type IndicatorEsfC7HealthTeamsClassificationTableColumns = {
	healthTeamName: Column;
	totalCervicalCancerCitizens: Column;
	totalCervicalCancerCitizensPercentage: Column;
	totalVaccinationCitizens: Column;
	totalVaccinationCitizensPercentage: Column;
	totalAttendanceCitizens: Column;
	totalAttendanceCitizensPercentage: Column;
	totalBreastCancerCitizens: Column;
	totalBreastCancerCitizensPercentage: Column;
	totalPercentage: Column;
	classificationPercentage: Column;
	classification: Column;
};

type IndicatorEsfC7HealthTeamsTableProps = {
	healthTeamsClassifications: ResumeIndicatorC7ByHealthTeamData[];
	isLoading: boolean;
};

const indicatorClassificationScores = getIndicatorClassificationScores(IndicatorEsfEnum.C7);

export function IndicatorEsfC7HealthTeamsClassificationTable({
	healthTeamsClassifications,
	isLoading,
}: Readonly<IndicatorEsfC7HealthTeamsTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC7HealthTeamsClassificationTableColumns>({
		healthTeamName: { title: "Equipe", visible: true },
		totalCervicalCancerCitizens: { title: "Exame câncer do cólo de útero", visible: true },
		totalCervicalCancerCitizensPercentage: {
			title: "Exame câncer do cólo de útero (%)",
			visible: true,
		},
		totalVaccinationCitizens: { title: "Vacinação HPV", visible: true },
		totalVaccinationCitizensPercentage: { title: "Vacinação HPV (%)", visible: true },
		totalAttendanceCitizens: { title: "Consultas", visible: true },
		totalAttendanceCitizensPercentage: { title: "Consultas (%)", visible: true },
		totalBreastCancerCitizens: { title: "Exame câncer de mama", visible: true },
		totalBreastCancerCitizensPercentage: { title: "Exame câncer de mama (%)", visible: true },
		totalPercentage: { title: "Percentual Concluído", visible: true },
		classificationPercentage: { title: "Classificação (%)", visible: true },
		classification: { title: "Classificação", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th
						className={`text-start px-2 ${!columns.healthTeamName.visible && "d-none"}`}
					>
						{columns.healthTeamName.title}
					</th>
					<th
						className={`px-2 ${
							!columns.totalCervicalCancerCitizens.visible && "d-none"
						}`}
					>
						{columns.totalCervicalCancerCitizens.title}
						<CardOverlayTrigger
							header={columns.totalCervicalCancerCitizens.title}
							message="Cidadãos que concluíram a prática / Cidadão identificados"
							icon={<FaInfoCircle className="ms-2" />}
						/>
					</th>
					<th
						className={`px-2 ${
							!columns.totalCervicalCancerCitizensPercentage.visible && "d-none"
						}`}
					>
						{columns.totalCervicalCancerCitizensPercentage.title}
					</th>
					<th className={`px-2 ${!columns.totalVaccinationCitizens.visible && "d-none"}`}>
						{columns.totalVaccinationCitizens.title}
						<CardOverlayTrigger
							header={columns.totalVaccinationCitizens.title}
							message="Cidadãos que concluíram a prática / Cidadão identificados"
							icon={<FaInfoCircle className="ms-2" />}
						/>
					</th>
					<th
						className={`px-2 ${
							!columns.totalVaccinationCitizensPercentage.visible && "d-none"
						}`}
					>
						{columns.totalVaccinationCitizensPercentage.title}
					</th>
					<th className={`px-2 ${!columns.totalAttendanceCitizens.visible && "d-none"}`}>
						{columns.totalAttendanceCitizens.title}
						<CardOverlayTrigger
							header={columns.totalAttendanceCitizens.title}
							message="Cidadãos que concluíram a prática / Cidadão identificados"
							icon={<FaInfoCircle className="ms-2" />}
						/>
					</th>
					<th
						className={`px-2 ${
							!columns.totalAttendanceCitizensPercentage.visible && "d-none"
						}`}
					>
						{columns.totalAttendanceCitizensPercentage.title}
					</th>
					<th
						className={`px-2 ${!columns.totalBreastCancerCitizens.visible && "d-none"}`}
					>
						{columns.totalBreastCancerCitizens.title}
						<CardOverlayTrigger
							header={columns.totalBreastCancerCitizens.title}
							message="Cidadãos que concluíram a prática / Cidadão identificados"
							icon={<FaInfoCircle className="ms-2" />}
						/>
					</th>
					<th
						className={`px-2 ${
							!columns.totalBreastCancerCitizensPercentage.visible && "d-none"
						}`}
					>
						{columns.totalBreastCancerCitizensPercentage.title}
					</th>
					<th className={`px-2 ${!columns.classificationPercentage.visible && "d-none"}`}>
						{columns.classificationPercentage.title}
					</th>
					<th className={`px-2 ${!columns.classification.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.classification.title}{" "}
							<CardOverlayTrigger
								header={"Classificação"}
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									<div className="text-primary">{`Ótimo: >= ${indicatorClassificationScores.awesome}%`}</div>
									<br />
									<div className="text-success">{`Bom: >= ${indicatorClassificationScores.good}% e < ${indicatorClassificationScores.awesome}%`}</div>
									<br />
									<div className="text-warning">
										{`Suficiente: >= ${indicatorClassificationScores.enough}% e < ${indicatorClassificationScores.good}%`}
									</div>
									<br />
									<div className="text-danger">{`Regular: < ${indicatorClassificationScores.enough}%`}</div>
									<br />
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC7HealthTeamsClassificationTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC7HealthTeamsClassificationTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC7HealthTeamsClassificationTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && healthTeamsClassifications.length > 0
					? healthTeamsClassifications.map((healthTeamClassification, index) => (
							<tr
								className="text-center"
								key={`health-team-classification-table-row-${index + 1}`}
							>
								<td
									className={`text-start px-2 ${
										!columns.healthTeamName.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{healthTeamClassification.healthTeam?.name ?? ""}</div>
										<div className="text-muted">
											{healthTeamClassification.healthTeam?.ine ?? ""}
										</div>
									</div>
								</td>
								<td
									className={`px-2 ${
										!columns.totalCervicalCancerCitizens.visible && "d-none"
									}`}
								>
									{`${
										healthTeamClassification.totalCervicalCancerCitizensFinished ??
										0
									} / ${
										healthTeamClassification.totalCervicalCancerCitizens ?? 0
									}`}
								</td>
								<td
									className={`px-2 ${
										!columns.totalCervicalCancerCitizens.visible && "d-none"
									}`}
								>
									{calculatePercentage(
										healthTeamClassification.totalCervicalCancerCitizensFinished,
										healthTeamClassification.totalCervicalCancerCitizens
									)}
									%
								</td>
								<td
									className={`px-2 ${
										!columns.totalVaccinationCitizens.visible && "d-none"
									}`}
								>
									{`${
										healthTeamClassification.totalVaccinationCitizensFinished ??
										0
									} / ${healthTeamClassification.totalVaccinationCitizens ?? 0}`}
								</td>
								<td
									className={`px-2 ${
										!columns.totalVaccinationCitizens.visible && "d-none"
									}`}
								>
									{calculatePercentage(
										healthTeamClassification.totalVaccinationCitizensFinished,
										healthTeamClassification.totalVaccinationCitizens
									)}
									%
								</td>
								<td
									className={`px-2 ${
										!columns.totalAttendanceCitizens.visible && "d-none"
									}`}
								>
									{`${
										healthTeamClassification.totalAttendanceCitizensFinished ??
										0
									} / ${healthTeamClassification.totalAttendanceCitizens ?? 0}`}
								</td>
								<td
									className={`px-2 ${
										!columns.totalAttendanceCitizens.visible && "d-none"
									}`}
								>
									{calculatePercentage(
										healthTeamClassification.totalAttendanceCitizensFinished,
										healthTeamClassification.totalAttendanceCitizens
									)}
									%
								</td>
								<td
									className={`px-2 ${
										!columns.totalBreastCancerCitizens.visible && "d-none"
									}`}
								>
									{`${
										healthTeamClassification.totalBreastCancerCitizensFinished ??
										0
									} / ${healthTeamClassification.totalBreastCancerCitizens ?? 0}`}
								</td>
								<td
									className={`px-2 ${
										!columns.totalBreastCancerCitizens.visible && "d-none"
									}`}
								>
									{calculatePercentage(
										healthTeamClassification.totalBreastCancerCitizensFinished,
										healthTeamClassification.totalBreastCancerCitizens
									)}
									%
								</td>
								<td
									className={`px-2 ${
										!columns.classificationPercentage.visible && "d-none"
									}`}
								>
									{`${healthTeamClassification.weightedPercentage}%`}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.classification.visible && "d-none"
									}`}
								>
									<Badge
										bg={getIndicatorClassificationColorClassName(
											IndicatorEsfEnum.C7,
											healthTeamClassification.weightedPercentage
										)}
									>
										{getIndicatorClassification(
											IndicatorEsfEnum.C7,
											healthTeamClassification.weightedPercentage
										)}
									</Badge>
								</td>
								<td></td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
